import { IconButton, Stack, Tooltip } from '@mui/material';
import { SharedReaderToolbarVariantProps, ToolbarItem } from '.';
import { useRef } from 'react';
import { getTabId, getTabPanelId, parseItemId } from '../menuTabUtils';
import { useTranslation } from 'react-i18next';

export default function ReaderToolbarVertical({
  activeId,
  onItemClick,
  items,
}: Readonly<SharedReaderToolbarVariantProps>) {
  const { t } = useTranslation();

  const firstTab = items[0];
  const lastTab = items[items.length - 1];
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function moveFocusToPreviousTab(target: HTMLElement) {
    const givenTabId = parseItemId(target.id);

    if (givenTabId === firstTab.id) {
      moveFocusToTab(lastTab);
    } else {
      const index = items.findIndex((item) => item.id === givenTabId);
      moveFocusToTab(items[index - 1]);
    }
  }

  function moveFocusToNextTab(target: HTMLElement) {
    const givenTabId = parseItemId(target.id);

    if (givenTabId === lastTab.id) {
      moveFocusToTab(firstTab);
    } else {
      const index = items.findIndex((item) => item.id === givenTabId);
      moveFocusToTab(items[index + 1]);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const target = event.currentTarget;
    let flag = false;

    switch (event.key) {
      case 'ArrowUp':
        moveFocusToPreviousTab(target as HTMLElement);
        flag = true;
        break;

      case 'ArrowDown':
        moveFocusToNextTab(target as HTMLElement);
        flag = true;
        break;

      case 'Home':
        moveFocusToTab(firstTab);
        flag = true;
        break;

      case 'End':
        moveFocusToTab(lastTab);
        flag = true;
        break;

      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  function moveFocusToTab(tab: ToolbarItem) {
    const index = items.findIndex((item) => item.id === tab.id);
    tabRefs.current[index]?.focus();
  }

  return (
    <Stack
      role='tablist'
      aria-orientation='vertical'
      sx={{
        minWidth: 'fit-content',
        paddingY: 3,
        paddingX: 1.5,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: '56',
        left: 0,
        gap: 1,
      }}
    >
      {items.map((item, index) => {
        const isActive = activeId === item.id;

        return (
          <Stack key={item.id} role='none' sx={{ listStyleType: 'none' }}>
            <Tooltip title={t(item.labelKey)} placement='right'>
              <IconButton
                role='tab'
                id={getTabId(item)}
                ref={(el: HTMLButtonElement | null) => {
                  tabRefs.current[index] = el;
                }}
                onClick={() => onItemClick(item.id)}
                {...(!isActive && {
                  tabIndex: -1,
                })}
                {...((isActive || !activeId) && {
                  tabIndex: 0,
                })}
                aria-label={t(item.labelKey)}
                aria-selected={isActive}
                aria-controls={getTabPanelId(item)}
                onKeyDown={handleKeyDown}
                sx={{
                  color: (theme) =>
                    isActive ? theme.palette.text.primary : 'default',
                  justifyContent: 'center',
                  minHeight: 40,
                  padding: 1,
                  ...(isActive && {
                    backgroundColor: (theme) => {
                      return theme.palette.primary.main + '14'; // '14' is hex value for 8% opacity
                    },
                  }),
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          </Stack>
        );
      })}
    </Stack>
  );
}
